/**
 * @fileoverview Servizio per partite (matches): creazione, approvazione, ELO.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  Timestamp,
  increment,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  COLLECTIONS,
  MATCHES_FIELDS,
  MATCH_STATUS,
  SET_FIELDS,
  MATCH_TEAM_FIELDS,
  GROUP_MEMBERS_FIELDS,
  GAME_FORMATS,
  RATING_IMPACT,
  MATCH_PARTICIPANT_FIELDS,
  ELO_CONFIG,
  RATINGS_FIELDS,
  LEGACY_TYPE_TO_GAME_FORMAT,
} from '../domain/dataModel';
import { calculateEloUpdates } from '../utils/eloCalculator';
import { getActiveMembers } from './groupService';
import { createRating, getRatingsByPeriod } from './ratingService';

/**
 * @param {Object} matchData
 * @returns {string}
 */
function _normalizeGameFormat(matchData) {
  const direct = matchData[MATCHES_FIELDS.GAME_FORMAT];
  if (direct) return direct;
  const t = matchData[MATCHES_FIELDS.TYPE];
  if (t && LEGACY_TYPE_TO_GAME_FORMAT[t]) return LEGACY_TYPE_TO_GAME_FORMAT[t];
  return GAME_FORMATS.MATCH;
}

/**
 * @param {Object} matchData
 * @returns {string[]}
 */
function _collectParticipantMemberIds(matchData) {
  const gameFormat = _normalizeGameFormat(matchData);
  const ids = new Set();
  if (gameFormat === GAME_FORMATS.ROTATION) {
    for (const p of matchData[MATCHES_FIELDS.PARTICIPANTS] || []) {
      if (p?.memberId) ids.add(p.memberId);
    }
    return [...ids];
  }
  const sets = matchData[MATCHES_FIELDS.SETS] || [];
  for (const s of sets) {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    [
      t1[MATCH_TEAM_FIELDS.PLAYER1],
      t1[MATCH_TEAM_FIELDS.PLAYER2],
      t2[MATCH_TEAM_FIELDS.PLAYER1],
      t2[MATCH_TEAM_FIELDS.PLAYER2],
    ].forEach((id) => id && ids.add(id));
  }
  if (gameFormat === GAME_FORMATS.COMPETITION) {
    for (const p of matchData[MATCHES_FIELDS.PARTICIPANTS] || []) {
      if (p?.memberId) ids.add(p.memberId);
    }
  }
  return [...ids];
}

/**
 * @param {Object} matchData
 * @param {Array<Object>} members
 * @returns {Map<string, string|null>}
 */
function _buildUserIdByMemberId(matchData, members) {
  const map = new Map();
  for (const m of members) {
    if (m.id && !m[GROUP_MEMBERS_FIELDS.IS_GUEST] && m[GROUP_MEMBERS_FIELDS.USER_ID]) {
      map.set(m.id, m[GROUP_MEMBERS_FIELDS.USER_ID]);
    }
  }
  for (const p of matchData[MATCHES_FIELDS.PARTICIPANTS] || []) {
    if (p?.memberId && p.userId != null && p.userId !== '') {
      map.set(p.memberId, p.userId);
    }
  }
  return map;
}

/**
 * @param {string} groupId
 * @param {string} periodId
 * @param {Array<Object>} members
 * @returns {Promise<Map<string, { rating: number, matchesPlayed: number, ref: import('firebase/firestore').DocumentReference }>>}
 */
async function _buildPlayerRatingsMap(groupId, periodId, members) {
  const ratings = await getRatingsByPeriod(groupId, periodId);
  const map = new Map();
  for (const r of ratings) {
    const ratingVal = Number(r[RATINGS_FIELDS.RATING]);
    const rv = Number.isFinite(ratingVal) ? ratingVal : ELO_CONFIG.RATING_BASE;
    const mp = Number(r[RATINGS_FIELDS.MATCHES_PLAYED]) || 0;
    const ref = doc(db, COLLECTIONS.RATINGS, r.id);
    const mid = r[RATINGS_FIELDS.MEMBER_ID];
    const uid = r[RATINGS_FIELDS.USER_ID];
    if (mid) map.set(mid, { rating: rv, matchesPlayed: mp, ref });
    if (uid) {
      const member = members.find(
        (m) => !m[GROUP_MEMBERS_FIELDS.IS_GUEST] && m[GROUP_MEMBERS_FIELDS.USER_ID] === uid
      );
      if (member) map.set(member.id, { rating: rv, matchesPlayed: mp, ref });
    }
  }
  return map;
}

/**
 * @param {string} groupId
 * @param {string} periodId
 * @param {string[]} memberIds
 * @param {Array<Object>} members
 * @param {Map<string, { rating: number, matchesPlayed: number, ref: import('firebase/firestore').DocumentReference }>} map
 */
async function _ensureRatingsForMembers(groupId, periodId, memberIds, members, map) {
  for (const memberId of memberIds) {
    if (map.has(memberId)) continue;
    const m = members.find((x) => x.id === memberId);
    if (m && m[GROUP_MEMBERS_FIELDS.IS_GUEST]) {
      const { ratingId } = await createRating(groupId, periodId, m.id, null, ELO_CONFIG.RATING_BASE);
      map.set(memberId, {
        rating: ELO_CONFIG.RATING_BASE,
        matchesPlayed: 0,
        ref: doc(db, COLLECTIONS.RATINGS, ratingId),
      });
    } else if (m && m[GROUP_MEMBERS_FIELDS.USER_ID]) {
      const uid = m[GROUP_MEMBERS_FIELDS.USER_ID];
      const { ratingId } = await createRating(groupId, periodId, null, uid, ELO_CONFIG.RATING_BASE);
      map.set(memberId, {
        rating: ELO_CONFIG.RATING_BASE,
        matchesPlayed: 0,
        ref: doc(db, COLLECTIONS.RATINGS, ratingId),
      });
    } else {
      const { ratingId } = await createRating(groupId, periodId, memberId, null, ELO_CONFIG.RATING_BASE);
      map.set(memberId, {
        rating: ELO_CONFIG.RATING_BASE,
        matchesPlayed: 0,
        ref: doc(db, COLLECTIONS.RATINGS, ratingId),
      });
    }
  }
}

/**
 * Esiti WIN/LOSS/DRAW per formato MATCH (stessa gerarchia dei set del calcolo ELO).
 * @param {Object} matchData
 * @returns {Map<string, 'WIN'|'LOSS'|'DRAW'>}
 */
function _computeMatchOutcomes(matchData) {
  const sets = matchData[MATCHES_FIELDS.SETS] || [];
  const outcomes = new Map();
  if (sets.length === 0) return outcomes;
  const first = sets[0];
  const t1 = first[SET_FIELDS.TEAM1] || {};
  const t2 = first[SET_FIELDS.TEAM2] || {};
  const teamA = [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2]].filter(Boolean);
  const teamB = [t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].filter(Boolean);
  if (teamA.length !== 2 || teamB.length !== 2) return outcomes;

  let gamesWonA = 0;
  let gamesWonB = 0;
  let setsWonA = 0;
  let setsWonB = 0;
  for (const set of sets) {
    const s1 = Number(set[SET_FIELDS.SCORE1]) || 0;
    const s2 = Number(set[SET_FIELDS.SCORE2]) || 0;
    gamesWonA += s1;
    gamesWonB += s2;
    if (s1 > s2) setsWonA += 1;
    else if (s2 > s1) setsWonB += 1;
  }

  let winner = 'draw';
  if (setsWonA > setsWonB) winner = 'A';
  else if (setsWonB > setsWonA) winner = 'B';
  else if (gamesWonA > gamesWonB) winner = 'A';
  else if (gamesWonB > gamesWonA) winner = 'B';

  const outcomeForTeam = (team) => {
    if (winner === 'draw') return 'DRAW';
    if (winner === 'A') return team === 'A' ? 'WIN' : 'LOSS';
    return team === 'B' ? 'WIN' : 'LOSS';
  };
  const oA = outcomeForTeam('A');
  const oB = outcomeForTeam('B');
  for (const id of teamA) outcomes.set(id, oA);
  for (const id of teamB) outcomes.set(id, oB);
  return outcomes;
}

/**
 * Finalizzazione dopo approvazione: snapshot ELO su match, aggiornamento PlayerRating.
 * @param {string} matchId
 * @param {Object} matchData
 */
async function _finalizeMatch(matchId, matchData) {
  const groupId = matchData[MATCHES_FIELDS.GROUP_ID];
  const periodId = matchData[MATCHES_FIELDS.PERIOD_ID];
  if (!groupId || !periodId) throw new Error('Gruppo o periodo mancante per finalizzare la partita.');

  const periodRef = doc(db, COLLECTIONS.PERIODS, periodId);
  const periodSnap = await getDoc(periodRef);
  if (!periodSnap.exists()) throw new Error('Periodo non trovato.');
  const period = { id: periodSnap.id, ...periodSnap.data() };

  const members = await getActiveMembers(groupId);
  const playerRatingsMap = await _buildPlayerRatingsMap(groupId, periodId, members);

  const memberIds = [...new Set(_collectParticipantMemberIds(matchData))];
  await _ensureRatingsForMembers(groupId, periodId, memberIds, members, playerRatingsMap);

  const gameFormat = _normalizeGameFormat(matchData);
  const matchForElo = { ...matchData, [MATCHES_FIELDS.GAME_FORMAT]: gameFormat };

  const ratingImpact = matchData[MATCHES_FIELDS.RATING_IMPACT] ?? RATING_IMPACT.OFFICIAL;
  const userIdByMemberId = _buildUserIdByMemberId(matchData, members);

  const matchRef = doc(db, COLLECTIONS.MATCHES, matchId);

  if (ratingImpact === RATING_IMPACT.OFFICIAL) {
    const playerRatingsForCalc = new Map();
    for (const [id, v] of playerRatingsMap) {
      playerRatingsForCalc.set(id, { rating: v.rating, matchesPlayed: v.matchesPlayed });
    }
    const eloResults = calculateEloUpdates(matchForElo, period, playerRatingsForCalc);
    const outcomes =
      gameFormat === GAME_FORMATS.MATCH ? _computeMatchOutcomes(matchData) : new Map();

    const participantsPayload = eloResults.map((r) => ({
      [MATCH_PARTICIPANT_FIELDS.MEMBER_ID]: r.memberId,
      [MATCH_PARTICIPANT_FIELDS.USER_ID]: userIdByMemberId.get(r.memberId) ?? null,
      [MATCH_PARTICIPANT_FIELDS.RATING_BEFORE]: r.ratingBefore,
      [MATCH_PARTICIPANT_FIELDS.RATING_AFTER]: r.ratingAfter,
      [MATCH_PARTICIPANT_FIELDS.DELTA]: r.delta,
    }));

    await runTransaction(db, async (tx) => {
      const mSnap = await tx.get(matchRef);
      if (!mSnap.exists()) return;
      tx.update(matchRef, {
        [MATCHES_FIELDS.PARTICIPANTS]: participantsPayload,
        [MATCHES_FIELDS.UPDATED_AT]: serverTimestamp(),
      });
      for (const r of eloResults) {
        const entry = playerRatingsMap.get(r.memberId);
        if (!entry) continue;
        const outcome = outcomes.get(r.memberId) ?? 'DRAW';
        const incW = gameFormat === GAME_FORMATS.MATCH && outcome === 'WIN' ? 1 : 0;
        const incL = gameFormat === GAME_FORMATS.MATCH && outcome === 'LOSS' ? 1 : 0;
        tx.update(entry.ref, {
          [RATINGS_FIELDS.RATING]: r.ratingAfter,
          [RATINGS_FIELDS.MATCHES_PLAYED]: increment(1),
          [RATINGS_FIELDS.WINS]: increment(incW),
          [RATINGS_FIELDS.LOSSES]: increment(incL),
          [RATINGS_FIELDS.UPDATED_AT]: serverTimestamp(),
        });
      }
    });
  } else {
    const participantsPayload = memberIds.map((mid) => {
      const entry = playerRatingsMap.get(mid);
      const rb = entry?.rating ?? ELO_CONFIG.RATING_BASE;
      return {
        [MATCH_PARTICIPANT_FIELDS.MEMBER_ID]: mid,
        [MATCH_PARTICIPANT_FIELDS.USER_ID]: userIdByMemberId.get(mid) ?? null,
        [MATCH_PARTICIPANT_FIELDS.RATING_BEFORE]: rb,
        [MATCH_PARTICIPANT_FIELDS.RATING_AFTER]: null,
        [MATCH_PARTICIPANT_FIELDS.DELTA]: null,
      };
    });

    await runTransaction(db, async (tx) => {
      const mSnap = await tx.get(matchRef);
      if (!mSnap.exists()) return;
      tx.update(matchRef, {
        [MATCHES_FIELDS.PARTICIPANTS]: participantsPayload,
        [MATCHES_FIELDS.UPDATED_AT]: serverTimestamp(),
      });
      for (const mid of memberIds) {
        const entry = playerRatingsMap.get(mid);
        if (!entry) continue;
        tx.update(entry.ref, {
          [RATINGS_FIELDS.MATCHES_PLAYED]: increment(1),
          [RATINGS_FIELDS.UPDATED_AT]: serverTimestamp(),
        });
      }
    });
  }
}

/**
 * Crea una nuova partita (status PENDING_APPROVAL, approvals con createdBy se presente).
 * @param {string} groupId
 * @param {string} periodId
 * @param {Object} matchData - { type, sets, playedAt, approvals?, gameFormat?, ratingImpact? } (playedAt Date o Timestamp)
 * @param {string} createdBy - UID chi crea
 * @returns {Promise<{ matchId: string }>}
 */
export async function createMatch(groupId, periodId, matchData, createdBy) {
  try {
    if (!groupId || !periodId || !createdBy) throw new Error('Gruppo, periodo o creatore mancante.');
    const playedAt = matchData.playedAt instanceof Date
      ? Timestamp.fromDate(matchData.playedAt)
      : matchData.playedAt || Timestamp.now();
    const approvals = matchData.approvals && matchData.approvals.length > 0
      ? matchData.approvals
      : [{ userId: createdBy, approvedAt: Timestamp.now() }];

    const ref = await addDoc(collection(db, COLLECTIONS.MATCHES), {
      [MATCHES_FIELDS.GROUP_ID]: groupId,
      [MATCHES_FIELDS.PERIOD_ID]: periodId,
      [MATCHES_FIELDS.TYPE]: matchData.type || 'classic',
      [MATCHES_FIELDS.GAME_FORMAT]: matchData.gameFormat || GAME_FORMATS.MATCH,
      [MATCHES_FIELDS.RATING_IMPACT]: matchData.ratingImpact || RATING_IMPACT.OFFICIAL,
      [MATCHES_FIELDS.PARTICIPANTS]: [],
      [MATCHES_FIELDS.STATUS]: MATCH_STATUS.PENDING_APPROVAL,
      [MATCHES_FIELDS.CREATED_BY]: createdBy,
      [MATCHES_FIELDS.CREATED_AT]: Timestamp.now(),
      [MATCHES_FIELDS.PLAYED_AT]: playedAt,
      [MATCHES_FIELDS.APPROVALS]: approvals,
      [MATCHES_FIELDS.SETS]: matchData.sets || [],
    });
    return { matchId: ref.id };
  } catch (err) {
    throw new Error(err?.message || 'Errore nel salvataggio della partita.');
  }
}

/**
 * Recupera una partita per ID.
 * @param {string} matchId
 * @returns {Promise<{ id: string, ...Object } | null>}
 */
export async function getMatchById(matchId) {
  try {
    if (!matchId) return null;
    const ref = doc(db, COLLECTIONS.MATCHES, matchId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento della partita.');
  }
}

/**
 * Recupera le ultime partite del gruppo, ordinate per playedAt desc.
 * @param {string} groupId
 * @param {number} [limitCount=10]
 * @returns {Promise<Array<{ id: string, ...Object }>>}
 */
export async function getRecentMatches(groupId, limitCount = 10) {
  try {
    if (!groupId) return [];
    const q = query(
      collection(db, COLLECTIONS.MATCHES),
      where(MATCHES_FIELDS.GROUP_ID, '==', groupId),
      orderBy(MATCHES_FIELDS.PLAYED_AT, 'desc'),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento delle partite.');
  }
}

/**
 * Recupera le partite in attesa di approvazione del gruppo.
 * @param {string} groupId
 * @returns {Promise<Array<{ id: string, ...Object }>>}
 */
export async function getPendingMatches(groupId) {
  try {
    if (!groupId) return [];
    const q = query(
      collection(db, COLLECTIONS.MATCHES),
      where(MATCHES_FIELDS.GROUP_ID, '==', groupId),
      where(MATCHES_FIELDS.STATUS, '==', MATCH_STATUS.PENDING_APPROVAL)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento delle partite in attesa.');
  }
}

/**
 * Recupera tutte le partite di un utente su tutti i suoi gruppi.
 * @param {string[]} groupIds - Lista di groupId a cui l'utente appartiene
 * @param {string} userId - UID dell'utente
 * @param {number} [limitCount=50]
 * @returns {Promise<Array<{ id: string, groupId: string, ...Object }>>}
 */
export async function getMatchesByUser(groupIds, userId, limitCount = 50) {
  if (!groupIds?.length || !userId) return [];
  try {
    const allMatches = [];
    for (const groupId of groupIds) {
      const q = query(
        collection(db, COLLECTIONS.MATCHES),
        where(MATCHES_FIELDS.GROUP_ID, '==', groupId),
        orderBy(MATCHES_FIELDS.PLAYED_AT, 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      snap.docs.forEach((d) => {
        const data = d.data();
        const sets = data[MATCHES_FIELDS.SETS] || [];
        const playerIds = new Set();
        sets.forEach((s) => {
          const t1 = s[SET_FIELDS.TEAM1] || {};
          const t2 = s[SET_FIELDS.TEAM2] || {};
          [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2],
            t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]]
            .forEach((id) => id && playerIds.add(id));
        });
        if (playerIds.has(userId)) {
          allMatches.push({ id: d.id, groupId, ...data });
        }
      });
    }
    allMatches.sort((a, b) => {
      const tA = a[MATCHES_FIELDS.PLAYED_AT] || a[MATCHES_FIELDS.CREATED_AT];
      const tB = b[MATCHES_FIELDS.PLAYED_AT] || b[MATCHES_FIELDS.CREATED_AT];
      return (tB?.toMillis?.() ?? 0) - (tA?.toMillis?.() ?? 0);
    });
    return allMatches;
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento delle partite utente.');
  }
}

/**
 * Calcola il numero di approvazioni richieste: Math.min(3, numero di giocatori reali).
 * Giocatore reale = non guest (userId != null e isGuest !== true).
 * @param {Object} match - Documento match con sets
 * @param {Array<Object>} members - Lista membri (con id, userId, isGuest)
 * @returns {number}
 */
export function getRequiredApprovals(match, members) {
  const sets = match[MATCHES_FIELDS.SETS] || [];
  const playerIds = new Set();
  sets.forEach((s) => {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]]
      .forEach((id) => id && playerIds.add(id));
  });
  const guestIds = new Set(
    (members || []).filter((m) => m[GROUP_MEMBERS_FIELDS.IS_GUEST]).map((m) => m.id)
  );
  let realCount = 0;
  playerIds.forEach((id) => {
    if (!guestIds.has(id)) realCount++;
  });
  return Math.min(3, realCount);
}

/**
 * Aggiunge l'approvazione dell'utente alla partita; se si raggiunge requiredApprovals, imposta status APPROVED e finalizza ELO.
 * @param {string} matchId
 * @param {string} userId - UID che approva
 * @param {Array<Object>} members - Membri attivi del gruppo (per getRequiredApprovals)
 * @returns {Promise<void>}
 */
export async function approveMatch(matchId, userId, members) {
  try {
    if (!matchId || !userId) throw new Error('Partita o utente mancante.');
    const matchRef = doc(db, COLLECTIONS.MATCHES, matchId);
    const matchSnap = await getDoc(matchRef);
    if (!matchSnap.exists()) throw new Error('Partita non trovata.');
    const matchData = { id: matchSnap.id, ...matchSnap.data() };
    const requiredApprovals = getRequiredApprovals(matchData, members || []);

    let shouldApplyElo = false;
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(matchRef);
      if (!snap.exists()) return;
      const data = snap.data();
      const approvals = data[MATCHES_FIELDS.APPROVALS] || [];
      if (approvals.some((a) => a.userId === userId)) return;
      const newApprovals = [...approvals, { userId, approvedAt: Timestamp.now() }];
      const updates = { [MATCHES_FIELDS.APPROVALS]: newApprovals };
      if (newApprovals.length >= requiredApprovals) {
        updates[MATCHES_FIELDS.STATUS] = MATCH_STATUS.APPROVED;
        shouldApplyElo = true;
      }
      tx.update(matchRef, updates);
    });

    if (shouldApplyElo) {
      const snap = await getDoc(matchRef);
      const data = snap.exists() ? snap.data() : null;
      if (data) await _finalizeMatch(matchId, { id: matchId, ...data });
    }
  } catch (err) {
    throw new Error(err?.message || 'Errore durante l\'approvazione.');
  }
}
